/**
 * Utilidades auxiliares para el manejo de actividades
 */

import { 
  ACTIVITY_TYPES, 
  createActivityEntry
} from './activityTypes';

// Re-exportar funciones de activityTypes para conveniencia
export { 
  getActivityIcon,
  getActivityColor,
  getActivityDescription,
  formatUserName,
  createActivityEntry,
  ACTIVITY_TYPES,
  EDIT_FIELDS
} from './activityTypes';

/**
 * Función centralizada para parsear timestamps en diferentes formatos
 * @param {string|Date} timestamp - Timestamp a parsear
 * @returns {Date} - Fecha parseada
 */
export const parseTimestamp = (timestamp) => {
  if (!timestamp) return new Date(0);
  
  try {
    // Formato nuevo con segundos "HH:MM:SS DD-MM-YYYY" (con 1 espacio)
    const newFormat = /^\d{2}:\d{2}:\d{2} \d{2}-\d{2}-\d{4}$/;
    if (typeof timestamp === 'string' && newFormat.test(timestamp)) {
      const [timeStr, dateStr] = timestamp.split(' ');
      const [hours, minutes, seconds] = timeStr.split(':').map(num => parseInt(num, 10));
      const [day, month, year] = dateStr.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day, hours, minutes, seconds);
    }
    
    // Formato estándar anterior "HH:MM DD-MM-YYYY" (con 1 espacio) - para compatibilidad
    const standardFormat = /^\d{2}:\d{2} \d{2}-\d{2}-\d{4}$/;
    if (typeof timestamp === 'string' && standardFormat.test(timestamp)) {
      const [timeStr, dateStr] = timestamp.split(' ');
      const [hours, minutes] = timeStr.split(':').map(num => parseInt(num, 10));
      const [day, month, year] = dateStr.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day, hours, minutes, 0);
    }
    
    // Formato legacy con múltiples espacios
    if (typeof timestamp === 'string' && timestamp.includes('     ')) {
      const parts = timestamp.split('     ');
      if (parts.length === 2) {
        const [timeStr, dateStr] = parts;
        const [hours, minutes] = timeStr.split(':').map(num => parseInt(num, 10));
        const dateParts = dateStr.split(/[/-]/).map(num => parseInt(num, 10));
        if (dateParts.length === 3) {
          return new Date(dateParts[2], dateParts[1] - 1, dateParts[0], hours, minutes, 0);
        }
      }
    }
    
    // Fallback: intentar parsear como fecha normal
    const fallbackDate = new Date(timestamp);
    return isNaN(fallbackDate.getTime()) ? new Date(0) : fallbackDate;
  } catch (e) {
    return new Date(0);
  }
};

/**
 * Formatea fecha para mostrar (oculta segundos)
 * @param {string|Date} dateString - Fecha a formatear
 * @returns {string} - Fecha formateada
 */
export const formatDate = (dateString) => {
  try {
    const date = parseTimestamp(dateString);
    
    if (isNaN(date.getTime()) || date.getTime() === 0) {
      return 'Fecha desconocida';
    }
    
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return 'Fecha inválida';
  }
};

/**
 * Ordena actividades por timestamp con lógica especial para creación
 * @param {Array} activities - Array de actividades
 * @returns {Array} - Actividades ordenadas (más reciente primero)
 */
export const sortActivities = (activities) => {
  return activities.sort((a, b) => {
    const dateA = parseTimestamp(a.timestamp);
    const dateB = parseTimestamp(b.timestamp);
    
    const timeA = dateA.getTime();
    const timeB = dateB.getTime();
    
    // Si los timestamps son diferentes, usar orden cronológico normal (más reciente primero)
    if (timeA !== timeB) {
      return timeB - timeA;
    }
    
    // Si los timestamps son iguales, priorizar la creación al final (primera cronológicamente)
    if (a.type === ACTIVITY_TYPES.CREATION && b.type !== ACTIVITY_TYPES.CREATION) {
      return 1; // a va después (más abajo en la lista visual)
    }
    if (b.type === ACTIVITY_TYPES.CREATION && a.type !== ACTIVITY_TYPES.CREATION) {
      return -1; // b va después (a va arriba)
    }
    
    return 0;
  });
};

/**
 * Filtra actividades redundantes (ej: asignación automática al comenzar a trabajar)
 * @param {Array} activities - Array de actividades
 * @returns {Array} - Actividades filtradas
 */
export const filterRedundantActivities = (activities) => {
  const activityGroups = {};
  
  // Agrupar actividades por timestamp
  activities.forEach(activity => {
    if (!activityGroups[activity.timestamp]) {
      activityGroups[activity.timestamp] = [];
    }
    activityGroups[activity.timestamp].push(activity);
  });
  
  const filteredActivities = [];
  
  // Para cada grupo de actividades con el mismo timestamp
  Object.values(activityGroups).forEach(group => {
    // Si hay cambio de estado a inProgress y asignación de usuario por la misma persona
    const hasStatusToInProgress = group.some(a => 
      a.type === ACTIVITY_TYPES.STATUS_CHANGE && 
      (a.newValue === 'inProgress' || a.newValue === 'inprogress' || a.newValue === 'in progress')
    );
    
    const hasAssigneeChange = group.some(a => 
      a.type === ACTIVITY_TYPES.ASSIGNEE_CHANGE && 
      a.oldValue === null && 
      a.updatedBy === a.newValue
    );
    
    // Si ambas actividades están presentes, filtrar la asignación
    if (hasStatusToInProgress && hasAssigneeChange) {
      group.forEach(activity => {
        if (activity.type !== ACTIVITY_TYPES.ASSIGNEE_CHANGE) {
          filteredActivities.push(activity);
        }
      });
    } else {
      // Si no es un caso especial, incluir todas las actividades
      filteredActivities.push(...group);
    }
  });
  
  return filteredActivities;
};

/**
 * Mapea tipos de actividad del formato antiguo al nuevo
 * @param {string} oldType - Tipo de actividad en formato antiguo
 * @returns {string} - Tipo de actividad en formato nuevo
 */
export const mapLegacyActivityType = (oldType) => {
  const typeMap = {
    'creation': ACTIVITY_TYPES.CREATION,
    'status_change': ACTIVITY_TYPES.STATUS_CHANGE,
    'task_discarded': ACTIVITY_TYPES.TASK_DISCARDED,
    'task_deleted': ACTIVITY_TYPES.TASK_DELETED,
    'task_restored': ACTIVITY_TYPES.TASK_RESTORED,
    'edit': ACTIVITY_TYPES.EDIT,
    'comment': ACTIVITY_TYPES.COMMENT_ADDED,
    'comment_edited': ACTIVITY_TYPES.COMMENT_EDITED,
    'evidence_comment': ACTIVITY_TYPES.EVIDENCE_COMMENT_ADDED,
    'comment_deleted': ACTIVITY_TYPES.COMMENT_DELETED,
    'assignee_change': ACTIVITY_TYPES.ASSIGNEE_CHANGE,
    'attachment': ACTIVITY_TYPES.ATTACHMENT,
    'deadline_change': ACTIVITY_TYPES.DEADLINE_CHANGE,
    'url': ACTIVITY_TYPES.URL_ADDED
  };
  
  return typeMap[oldType] || oldType;
};

/**
 * Convierte actividad del historial unificado al formato del componente
 * @param {object} activity - Actividad del historial unificado
 * @param {number} index - Índice de la actividad
 * @returns {object} - Actividad en formato del componente
 */
export const mapUnifiedActivityToComponent = (activity, index) => {
  let mappedType = mapLegacyActivityType(activity.type);
  
  return {
    id: `activity_${index}`,
    type: mappedType,
    user: activity.updatedBy || 'Usuario',
    timestamp: activity.timestamp,
    field: activity.field,
    oldValue: activity.oldValue,
    newValue: activity.newValue
  };
};

/**
 * Procesa el historial de actividades de una tarea
 * @param {object} task - Tarea con historial
 * @returns {Array} - Array de actividades procesadas
 */
export const processTaskActivityHistory = (task) => {
  let activities = [];
  
  // Usar el historial unificado si existe
  if (task.activityHistory && Array.isArray(task.activityHistory) && task.activityHistory.length > 0) {
    // Mapear actividades del historial unificado
    const mappedActivities = task.activityHistory.map((activity, index) => 
      mapUnifiedActivityToComponent(activity, index)
    );
    
    // Filtrar actividades redundantes
    activities = filterRedundantActivities(mappedActivities);
  } else {
    // Fallback para tareas antiguas sin historial unificado
    activities = buildLegacyActivityHistory(task);
  }
  
  // Ordenar actividades
  return sortActivities(activities);
};

/**
 * Construye historial de actividades para tareas antiguas (fallback)
 * @param {object} task - Tarea sin historial unificado
 * @returns {Array} - Array de actividades construidas
 */
const buildLegacyActivityHistory = (task) => {
  const activities = [];
  
  // Añadir actividad de creación
  if (task.createdBy && task.createdTimestamp) {
    activities.push({
      id: 'creation',
      type: ACTIVITY_TYPES.CREATION,
      user: task.createdBy,
      timestamp: task.createdTimestamp,
      field: 'creation',
      oldValue: null,
      newValue: task.title
    });
  }
  
  // Añadir actualizaciones de estado si existe historial
  if (task.statusHistory && Array.isArray(task.statusHistory)) {
    task.statusHistory.forEach((statusChange, index) => {
      activities.push({
        id: `status_${index}`,
        type: ACTIVITY_TYPES.STATUS_CHANGE,
        user: statusChange.updatedBy || 'Usuario',
        timestamp: statusChange.timestamp,
        field: 'status',
        oldValue: statusChange.oldStatus,
        newValue: statusChange.newStatus
      });
    });
  }
  
  // Añadir otros historiales...
  // (assigneeHistory, deadlineHistory, editHistory, comments)
  
  return activities;
};

/**
 * Cuenta actividades no relacionadas con comentarios
 * @param {object} task - Tarea
 * @returns {number} - Número de actividades
 */
export const getActivityCount = (task) => {
  if (task.activityHistory && Array.isArray(task.activityHistory)) {
    const nonCommentActivities = task.activityHistory.filter(activity => 
      activity.type !== ACTIVITY_TYPES.COMMENT_ADDED && 
      activity.type !== ACTIVITY_TYPES.COMMENT && 
      activity.type !== ACTIVITY_TYPES.EVIDENCE_COMMENT_ADDED
    );
    return nonCommentActivities.length;
  }
  
  // Fallback para tareas antiguas
  let count = 0;
  if (task.statusHistory) count += task.statusHistory.length;
  if (task.assigneeHistory) count += task.assigneeHistory.length;
  if (task.deadlineHistory) count += task.deadlineHistory.length;
  if (task.editHistory) count += task.editHistory.length;
  
  return count;
};

/**
 * Crea entrada de actividad para cambio de estado
 * @param {object} task - Tarea
 * @param {string} newStatus - Nuevo estado
 * @param {string} userEmail - Email del usuario
 * @param {string} timestamp - Timestamp
 * @returns {object} - Entrada de actividad
 */
export const createStatusChangeActivity = (task, newStatus, userEmail, timestamp) => {
  let activityType = ACTIVITY_TYPES.STATUS_CHANGE;
  
  // Si estamos pasando de inProgress a todo, es un descarte
  if (task.status === 'inProgress' && newStatus === 'todo') {
    activityType = ACTIVITY_TYPES.TASK_DISCARDED;
  }
  
  return createActivityEntry(activityType, userEmail, timestamp, {
    field: 'status',
    oldValue: task.status,
    newValue: newStatus
  });
};

/**
 * Crea entrada de actividad para cambio de asignación
 * @param {string} oldAssignee - Asignado anterior
 * @param {string} newAssignee - Nuevo asignado
 * @param {string} userEmail - Email del usuario
 * @param {string} timestamp - Timestamp
 * @returns {object} - Entrada de actividad
 */
export const createAssigneeChangeActivity = (oldAssignee, newAssignee, userEmail, timestamp) => {
  return createActivityEntry(ACTIVITY_TYPES.ASSIGNEE_CHANGE, userEmail, timestamp, {
    field: 'assignee',
    oldValue: oldAssignee,
    newValue: newAssignee
  });
};

/**
 * Crea entrada de actividad para comentario
 * @param {string} commentText - Texto del comentario
 * @param {string} userEmail - Email del usuario
 * @param {string} timestamp - Timestamp
 * @param {boolean} isEvidence - Si es evidencia de finalización
 * @returns {object} - Entrada de actividad
 */
export const createCommentActivity = (commentText, userEmail, timestamp, isEvidence = false) => {
  const activityType = isEvidence ? ACTIVITY_TYPES.EVIDENCE_COMMENT_ADDED : ACTIVITY_TYPES.COMMENT_ADDED;
  
  return createActivityEntry(activityType, userEmail, timestamp, {
    field: 'comment',
    oldValue: null,
    newValue: commentText
  });
};